import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, Sparkles, Zap, Award, TrendingUp, CheckCircle2, Building2, Lightbulb, Users2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { fetchServices } from '../lib/contentApi';
import { resolveLucideIcon } from '../lib/lucideIcon';

const publicUrl = process.env.PUBLIC_URL || '';
const backgroundVideoSrc = `${publicUrl}/background-video.mp4`;
const homepagePhotoSrc = `${publicUrl}/photo-homepage.avif`;

export const HomeAlt = () => {
  const videoRef = useRef(null);
  const [services, setServices] = useState([]);

  useEffect(() => {
    fetchServices()
      .then(setServices)
      .catch(() => setServices([]));
  }, []);

  const technologies = [
    {
      icon: Lightbulb,
      title: 'LED de Última Geração',
      description: 'Tecnologia que reduz até 80% do consumo energético mantendo qualidade superior de luz'
    },
    {
      icon: Zap,
      title: 'Sistemas Inteligentes',
      description: 'Controlo automatizado com sensores de presença e luz natural para máxima eficiência'
    },
    {
      icon: Building2,
      title: 'Gestão BMS/DALI',
      description: 'Integração total com sistemas de gestão predial para controlo centralizado'
    },
    {
      icon: Users2,
      title: 'Human Centric Lighting',
      description: 'Iluminação que se adapta ao ritmo circadiano para melhor bem-estar'
    }
  ];

  const particles = React.useMemo(() =>
    [...Array(30)].map((_, i) => ({
      id: i,
      width: Math.random() * 4 + 1 + 'px',
      height: Math.random() * 4 + 1 + 'px',
      top: Math.random() * 100 + '%',
      left: Math.random() * 100 + '%',
      delay: Math.random() * 5 + 's',
      duration: Math.random() * 10 + 10 + 's'
    })), []);

  useEffect(() => {
    const cursor = document.getElementById('custom-cursor');
    const handleMouseMove = (e) => {
      if (cursor) {
        cursor.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0) translate(-50%, -50%) scale(1.2)`;
      }
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const playVideo = () => {
      video.play().catch(() => {});
    };

    playVideo();
    video.addEventListener('loadeddata', playVideo);

    return () => video.removeEventListener('loadeddata', playVideo);
  }, []);

  return (
    <div className="min-h-screen bg-[#f2f1ec] overflow-hidden">
      {/* Custom Cursor */}
      <div
        id="custom-cursor"
        className="hidden lg:block fixed top-0 left-0 w-6 h-6 border-2 border-black rounded-full pointer-events-none z-[60] mix-blend-difference transition-transform duration-75 ease-out"
        style={{ transform: 'translate3d(-100px, -100px, 0) scale(1.2)' }}
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            poster={homepagePhotoSrc}
            src={backgroundVideoSrc}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>

        {/* Animated Particles */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {particles.map((p) => (
            <div
              key={p.id}
              className="absolute bg-white rounded-full"
              style={{
                width: p.width,
                height: p.height,
                top: p.top,
                left: p.left,
                animation: `float ${p.duration} infinite ease-in-out ${p.delay}`
              }}
            />
          ))}
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-12 text-white">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 leading-tight sm:leading-none">
            Trazemos luz ao seu projeto
          </h1>

          <p className="text-base sm:text-lg text-neutral-200 mb-10 max-w-3xl">
            Transformamos a iluminação do seu espaço com soluções inovadoras, flexíveis e ajustadas às necessidades de cada cliente.
          </p>

          <div className="flex flex-row gap-2">
            <Link to="/catalogos">
              <Button
                size="lg"
                className="bg-white text-black hover:bg-neutral-200 px-8 py-6 text-base font-semibold group relative overflow-hidden shadow-xl border-2 border-transparent"
              >
                <span className="relative z-10 flex items-center gap-2">
                  Ver Catálogos
                  <ArrowUpRight className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" size={20} />
                </span>
              </Button>
            </Link>
            <Link to="/contactos">
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-6 text-base font-semibold backdrop-blur-sm"
              >
                Fale Connosco
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-20">
          <div className="flex flex-col items-center gap-2 animate-bounce">
            <span className="text-white text-xs uppercase tracking-wider">Scroll</span>
            <div className="w-px h-16 bg-gradient-to-b from-white via-white/50 to-transparent" />
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold mb-2">
              Tecnologia de Ponta
            </h2>
            <p className="text-base text-[#262d37] max-w-3xl mx-auto">
              Implementamos as soluções mais avançadas do mercado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {technologies.map((tech, index) => {
              const Icon = tech.icon;
              return (
                <div
                  key={index}
                  className="group"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Card className="h-full bg-[#f2f1ec] transition-all duration-300 hover:shadow-xl transform hover:-translate-y-2">
                    <CardContent className="p-6 text-center">
                      <div className="w-16 h-16 mx-auto mb-5 bg-[#262d37] rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-6 transition-all">
                        <Icon size={32} className="text-white" />
                      </div>
                      <h3 className="text-xl font-bold mb-2 text-[#262d37]">
                        {tech.title}
                      </h3>
                      <p className="text-sm text-[#262d37] leading-relaxed">
                        {tech.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-[#f2f1ec]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mb-16">
            <div>
              <span className="inline-block px-3 py-1 bg-[#262d37] text-white text-xs font-semibold rounded-full mb-4">
                O QUE FAZEMOS
              </span>
              <h2 className="text-2xl md:text-3xl font-bold mb-2 leading-tight text-[#262d37]">
                Soluções de Iluminação à Medida
              </h2>
              <p className="text-base text-[#262d37] leading-relaxed mb-8">
                Acompanhamos cada projeto do conceito à execução, criando soluções de iluminação eficientes, funcionais e esteticamente diferenciadoras.
                O nosso foco é valorizar os espaços e otimizar o consumo energético.
              </p>
              <div className="space-y-4 mb-8">
                {['Consultoria Especializada', 'Gestão de Projeto', 'Fornecimento Premium', 'Suporte Contínuo'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 className="text-green-600" size={20} />
                    <span className="text-base font-medium">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/sobre">
                <Button className="bg-[#262d37] text-white hover:bg-[#262d37]/80 px-8 py-5 text-base group">
                  Saber Mais
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="relative z-10">
                <img
                  src={homepagePhotoSrc}
                  alt="Serviços de iluminação LuxDall"
                  onLoad={(e) => e.target.classList.add('img-loaded')}
                  className="w-full h-[350px] md:h-[450px] object-cover rounded-2xl shadow-xl img-smooth"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-full h-full border-2 border-black rounded-2xl -z-0" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((service) => {
              const Icon = resolveLucideIcon(service.icon, Sparkles);

              return (
                <div
                  key={service.id}
                  className="group cursor-pointer"
                >
                  <Card className="h-full border-2 border-[#262d37] hover:border-black transition-all duration-300 bg-white hover:shadow-xl transform hover:-translate-y-1 hover:scale-[1.02]">
                    <CardContent className="p-6 md:p-8">
                      <Icon
                        size={40}
                        className="mb-5 text-black transition-transform group-hover:scale-110 group-hover:rotate-6"
                      />
                      <h3 className="text-xl font-bold mb-2 text-[#262d37]">
                        {service.title}
                      </h3>
                      <p className="text-sm text-[#262d37] leading-relaxed mb-5">
                        {service.description}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-neutral-900 via-black to-neutral-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '60px 60px'
          }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-xs font-semibold border border-white/30 mb-4">
            COMECE HOJE
          </span>
          <h2 className="text-2xl md:text-3xl font-bold mb-3 leading-tight">
            Transforme o seu próximo Projeto
          </h2>
          <p className="text-base md:text-lg text-neutral-300 mb-10 max-w-xl mx-auto">
            Junte-se a mais de 50 empresas que já transformaram os seus espaços connosco.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <Link to="/contactos">
              <Button size="lg" className="w-full sm:w-auto bg-white text-black hover:bg-neutral-200 px-8 py-6 text-base font-semibold group shadow-xl border-2 border-transparent">
                Solicitar Orçamento Gratuito
                <ArrowUpRight size={20} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/catalogos">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-white text-white hover:bg-white hover:text-black px-8 py-6 text-base font-semibold">
                Explorar Catálogos
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) translateX(0); }
          50% { transform: translateY(-20px) translateX(10px); }
        }

      `}</style>
    </div>
  );
};
